import React, { useEffect, useState } from 'react';
import { generateDateTimeSlots } from 'src/utils/recruiter.util';
import {
	CampusRoundDocument,
	CampusScheduleInterviewRoundInput,
	CampusUserWithApplication,
	ScheduleInterviewRoundInput,
	UIDateSlot,
} from 'types';
import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'src/context/ToastProvider';
import { get } from 'lodash';
import DateSlots from 'src/components/recruiterPositions/schedule-interview/DateSlots';
import TimeSlots from 'src/components/recruiterPositions/schedule-interview/TimeSlots';
import CampusUserInterviewForm from './CampusUserInterviewForm';
import {
	campusRescheduleInterviewRoundForApplication,
	campusScheduleInterviewRoundForApplication,
} from 'src/apis/campusHiring';
import moment from 'moment';

interface Props {
	onFinish?: () => void;
	isReschedule?: boolean;
	studentData?: CampusUserWithApplication;
	ongoingRound?: CampusRoundDocument;
}

const CampusUserInterview = ({ studentData, onFinish, isReschedule, ongoingRound }: Props) => {
	const queryClient = useQueryClient();
	const { dates, times, todaysTimes } = generateDateTimeSlots(1);

	const [scheduleInterViewRoundInput, setScheduleInterViewRoundInput] = useState<CampusScheduleInterviewRoundInput>({
		userId: studentData?._id?.toString() || '',
		startTime: new Date(),
		duration: ongoingRound?.duration || 0,
		message: ongoingRound?.message || '',
		interviewLink: ongoingRound?.interviewLink || '',
		interviewers: ongoingRound?.interviewers || [],
	});

	const [selectedDateSlot, setSelectedDateSlot] = useState<UIDateSlot>();
	const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');

	useEffect(() => {
		if (!selectedDateSlot || !selectedTimeSlot) {
			return;
		}
		let startDateAndTime = new Date(selectedDateSlot?.fullDate);
		// Extract the hour and minute from the time string
		let [timePart, modifier] = selectedTimeSlot.split(' ');
		let [hours, minutes] = timePart.split(':').map(str => parseInt(str, 10));
		// Convert 12-hour time to 24-hour format
		if (modifier === 'PM' && hours < 12) {
			hours += 12;
		} else if (modifier === 'AM' && hours === 12) {
			hours = 0;
		}
		// Set the hours and minutes to the date object
		startDateAndTime.setHours(hours, minutes, 0, 0); // Resetting seconds and milliseconds
		setScheduleInterViewRoundInput(prev => ({ ...prev, startTime: startDateAndTime }));
	}, [selectedDateSlot, selectedTimeSlot]);
	const toast = useToast();

	const scheduleInterviewFn = useMutation(
		['campusScheduleInterviewRoundForApplication'],
		campusScheduleInterviewRoundForApplication,
		{
			onSuccess() {
				queryClient.refetchQueries(['getCampusHiringCandidates']);
				queryClient.refetchQueries({
					queryKey: ['getCampusHiringApplicationsWithCandidates'],
					exact: false, // This allows refetching of all matching queries
				});
				toast({
					title: 'Interview scheduled successfully',
					type: 'success',
				});
				onFinish && onFinish();
			},
			onError(error: Error) {
				const errorMessage = get(error, ['response', 'data', 'message'], error.message);
				toast({
					title: `Error occured while scheduling interview ${errorMessage}`,
					type: 'error',
				});
			},
		},
	);
	const rescheduleInterviewFn = useMutation(
		['rescheduleInterviewRoundForApplication'],
		campusRescheduleInterviewRoundForApplication,
		{
			onSuccess() {
				queryClient.refetchQueries(['getCampusHiringCandidates']);
				queryClient.refetchQueries({
					queryKey: ['getCampusHiringApplicationsWithCandidates'],
					exact: false, // This allows refetching of all matching queries
				});
				toast({
					title: 'Interview rescheduled successfully',
					type: 'success',
				});
				onFinish && onFinish();
			},
			onError(error: Error) {
				const errorMessage = get(error, ['response', 'data', 'message'], error.message);
				toast({
					title: `Error occured while rescheduling interview ${errorMessage}`,
					type: 'error',
				});
			},
		},
	);

	useEffect(() => {
		setSelectedTimeSlot('');
	}, [selectedDateSlot]);

	return (
		<div className="flex flex-col gap-[20px]">
			<p className="font-inter text-[18px] font-[500] leading-[28px]">
				{isReschedule ? 'Reschedule Interview for:' : 'Interviewing:'}{' '}
				<span className="font-[700]">{studentData?.name || studentData?.email}</span>
			</p>
			<DateSlots
				dates={dates}
				times={times}
				selectedDateSlot={selectedDateSlot}
				setSelectedDateSlot={setSelectedDateSlot}
				setSelectedTimeSlot={setSelectedTimeSlot}
			/>
			<TimeSlots
				dates={dates}
				times={times}
				todaysTimes={todaysTimes}
				selectedDateSlot={selectedDateSlot}
				selectedTimeSlot={selectedTimeSlot}
				setSelectedTimeSlot={setSelectedTimeSlot}
			/>
			<CampusUserInterviewForm
				isReschedule={isReschedule}
				campusApplicationId={studentData?.application?._id}
				scheduleInterViewRoundInput={scheduleInterViewRoundInput}
				setScheduleInterViewRoundInput={setScheduleInterViewRoundInput}
				scheduleInterviewFn={scheduleInterviewFn}
				disable={
					!selectedDateSlot || !selectedTimeSlot || scheduleInterviewFn?.isLoading || rescheduleInterviewFn?.isLoading
				}
				rescheduleInterviewFn={rescheduleInterviewFn}
			/>
		</div>
	);
};

export default CampusUserInterview;
